import { spellService } from "../services/spellService";
import { BuffOrDebuff, Warband, WarbandLevelUp } from "../types";

/**
 * Format a weapon or spell name with damage modifier
 * @param name - The base name
 * @param damageModifier - Optional damage modifier (positive or negative)
 * @returns Formatted string with damage modifier in parentheses if present
 */
export const formatDamageModifier = (
  name: string,
  damageModifier?: number | null
): string => {
  if (damageModifier !== null && damageModifier !== undefined) {
    return `${name} (${damageModifier >= 0 ? '+' : ''}${damageModifier})`;
  }
  return name;
};

export const getBuffName = (
  buffOrDebuff: BuffOrDebuff
): string => {
  if (buffOrDebuff.name) {
    return buffOrDebuff.name;
  }
  if (buffOrDebuff.sourceId) {
    return buffOrDebuff.sourceId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return buffOrDebuff.id
    .replace(/^[a-z]+_/, '') // strip leading "spell_" prefix etc.
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatBuffEffect = (buff: BuffOrDebuff): string => {
  const parts: string[] = [];
  if (buff.moveChangeValue != null) parts.push(`${buff.moveChangeType === 'set' ? '=' : buff.moveChangeValue >= 0 ? '+' : ''}${buff.moveChangeValue} Move`);
  if (buff.fightChangeValue != null) parts.push(`${buff.fightChangeType === 'set' ? '=' : buff.fightChangeValue >= 0 ? '+' : ''}${buff.fightChangeValue} Fight`);
  if (buff.shootChangeValue != null) parts.push(`${buff.shootChangeType === 'set' ? '=' : buff.shootChangeValue >= 0 ? '+' : ''}${buff.shootChangeValue} Shoot`);
  if (buff.armourChangeValue != null) parts.push(`${buff.armourChangeType === 'set' ? '=' : buff.armourChangeValue >= 0 ? '+' : ''}${buff.armourChangeValue} Armour`);
  if (buff.willChangeValue != null) parts.push(`${buff.willChangeType === 'set' ? '=' : buff.willChangeValue >= 0 ? '+' : ''}${buff.willChangeValue} Will`);
  if (buff.healthChangeValue != null) parts.push(`${buff.healthChangeType === 'set' ? '=' : buff.healthChangeValue >= 0 ? '+' : ''}${buff.healthChangeValue} Health`);
  if (buff.diceRollChangeValue != null) parts.push(`${buff.diceRollChangeValue >= 0 ? '+' : ''}${buff.diceRollChangeValue} ${buff.diceRollChangeType ?? 'Roll'}`);
  return parts.length > 0 ? parts.join(', ') : 'No stat changes';
};


export const getBuffSourceDescription = async (warbandId: string, buff: BuffOrDebuff) => {
  if (buff.source === "spell") {
    const allSpellsResponse = await spellService.getAllAvailableForWarband(warbandId);
    const spells = (allSpellsResponse.data || []);
    const spell = spells.find(s => s.id === buff.sourceId);

    return spell?.description ?? "";
  }

  return "";
};

export const getWizardLevelDescription = (warband: Warband): string => {
  // Wizards start at level 0 (Frostgrave core rules).
  const wizardLevel = Math.floor(warband.experience / 100) - (warband.apprenticePromotions * 6);
  return `Level ${wizardLevel}`;
}

export const computeUnusedLevelUps = (warband: Warband, levelUps: WarbandLevelUp[]): number => {
  const totalSlots = Math.max(0, Math.floor(warband.experience / 100));
  const used = levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned).length;
  return Math.max(0, totalSlots - used);
}

export const computeLevelUpStatBonuses = (levelUps: WarbandLevelUp[]): { fight: number; shoot: number; will: number; health: number } => {
  const bonuses = { fight: 0, shoot: 0, will: 0, health: 0 };
  for (const lu of levelUps) {
    if (!lu.statsIncrease) continue;
    switch (lu.statsIncrease) {
      case 'F': bonuses.fight += 1; break;
      case 'S': bonuses.shoot += 1; break;
      case 'W': bonuses.will += 1; break;
      case 'H': bonuses.health += 1; break;
    }
  }
  return bonuses;
}

const pluralizeLastWord = (label: string): string => {
  const words = label.split(' ');
  const lastWord = words[words.length - 1];

  if (/s$/i.test(lastWord)) {
    return label;
  }

  const pluralLastWord = /[^aeiou]y$/i.test(lastWord)
    ? `${lastWord.slice(0, -1)}ies`
    : `${lastWord}s`;

  words[words.length - 1] = pluralLastWord;
  return words.join(' ');
};

export const formatSoldierType = (
  type?: string | null,
  pluralize: boolean = false
): string => {
  if (!type || !type.trim()) {
    return 'Unknown';
  }
  if (type === "Temporary") {
    return pluralize ? "Temporary Soldiers" : "Temporary Soldier";
  }

  const label = type
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

  return pluralize ? pluralizeLastWord(label) : label;
};
